import { DataTable } from '@/components/dataTable';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTablePagination } from '@/components/dataTable/Pagination';
import Select from '@/components/dataTable/TableSelect';
import { Button } from '@/design-system/button';
import { Input } from '@/design-system/input';
import { useDynamicLocale } from '@/hoslog-intl';
import { CsvIcon } from '@/icons/csv-icon';
import { formatDate } from '@/lib/dateFormatter';
import { exportCsvSingleFilePapa, filterCsvDataByVisibleColumns } from '@/lib/exportCsv';
import { formatAmount } from '@/lib/format';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import type { AffiliateReferralDetail } from '@/services/dashboard/referralReports/referralReports.types';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { SearchIcon } from 'lucide-react';
import * as React from 'react';
import { getReferralTypeLabel } from '../../utils/getReferralTypeLabel';
import { getAffiliateNetworkColumns } from './Column';

export function AffiliateNetworkReferralTable({
  data,
  pagination,
  isLoading = false,
}: {
  data: AffiliateReferralDetail[];
  pagination: Pagination;
  isLoading?: boolean;
}) {
  const { t } = useDynamicLocale();
  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );

  const exportKeysByColumn = React.useMemo(
    () => ({
      sn: [t('common.page.tableColumn.sn')],
      affiliateInfo: [t('common.page.tableColumn.userInfo')],
      reservedBy: [t('common.page.tableColumn.reservedBy')],
      referralType: [t('common.page.tableColumn.referralType')],
      createdDate: [t('common.page.tableColumn.visitDateTime')],
      rewardPoints: [t('common.page.tableColumn.rewardPoints')],
    }),
    [t],
  );

  const { referralType } = useSearch({
    from: '/(app)/dashboard/referral-reports/$reportType/$referrerId/',
  });
  const navigate = useNavigate({ from: '/dashboard/referral-reports/$reportType/$referrerId' });

  const handleExport = () => {
    const exportData = table.getFilteredRowModel().rows.map(({ original: row, index }) => ({
      [t('common.page.tableColumn.sn')]: getSerialNumber(table, index),
      [t('common.page.tableColumn.userInfo')]: [row.affiliateInfo, row.emailAddress].filter(Boolean).join(', '),
      [t('common.page.tableColumn.reservedBy')]: row.reservedBy,
      [t('common.page.tableColumn.referralType')]:
        `${t('common.page.table.affiliateIntroduction')} (${getReferralTypeLabel(row.referralType, t) ?? '-'})`,
      [t('common.page.tableColumn.visitDateTime')]: formatDate(row.visitDateTime, 'YYYY/MM/DD HH:mm:ss'),
      [t('common.page.tableColumn.rewardPoints')]: formatAmount(row.rewardPoints ?? 0),
    }));
    const visibleColumnIds = table.getVisibleLeafColumns().map((column) => column.id);
    const visibleData = filterCsvDataByVisibleColumns(exportData, visibleColumnIds, exportKeysByColumn);

    exportCsvSingleFilePapa(visibleData, {
      fileName: 'affiliate-network-referral-report',
      format: 'YYYY/MM/DD HH:mm:ss',
    });
  };

  const columns = React.useMemo(() => getAffiliateNetworkColumns(t), [t]);
  const { table } = useDataTable({
    data,
    columns,
    pageCount,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
    },
    meta: {
      defaultDatePreset: 'today',
      title: t('dashboard.referralReports.tableTittle.affiliateRecords'),
      totalPage: pagination.totalItems,
      pageSize: pagination.pageSize,
      getSerialNumber: (rowIndex: number) =>
        Math.max((pagination.currentPage ?? 1) - 1, 0) * (pagination.pageSize ?? 10) + rowIndex + 1,
      actionDisabled: data.length === 0,
      loading: isLoading,
    },
    getRowId: (row, index) =>
      [
        row.affiliateId,
        row.visitDateTime,
        row.reservedBy,
        row.referralType,
        Math.max((pagination.currentPage ?? 1) - 1, 0) * (pagination.pageSize ?? 10) + index,
      ].join('-'),
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <DataTable table={table}>
      <div>
        <div className="flex flex-wrap items-center gap-3 border-b border-border-divider px-4 py-3">
          <h2 className="mr-auto text-md font-bold text-primary">
            {t('dashboard.referralReports.tableTittle.referReport')}
          </h2>
          <div className="flex items-center rounded border border-border-divider px-2">
            <SearchIcon className="text-secondary stroke-1.5" size={20} />
            <Input
              placeholder={t('common.page.search')}
              value={(table.getState().globalFilter as string) ?? ''}
              onChange={(event) => table.setGlobalFilter(event.target.value)}
              className="h-8 w-40 border-0 shadow-none outline-none focus-visible:ring-0 lg:w-56"
            />
          </div>
          <Select
            label={t('common.page.tableColumn.referralType')}
            value={referralType ?? 'all'}
            onValueChange={(value) =>
              void navigate({
                search: (previous) => ({
                  ...previous,
                  referralType: value === 'all' ? undefined : (value as 'direct' | 'indirect'),
                  page: 0,
                }),
              })
            }
            options={[
              { label: t('common.form.label.all'), value: 'all' },
              {
                label: `${t('common.page.table.affiliateIntroduction')} (${t('common.page.table.direct')})`,
                value: 'direct',
              },
              {
                label: `${t('common.page.table.affiliateIntroduction')} (${t('common.page.table.indirect')})`,
                value: 'indirect',
              },
            ]}
            hideDefaultOption
            className="!w-[136px] [&>span]:truncate [&>span]:text-left [&>span]:block"
          />
          <Button type="button" onClick={handleExport}>
            <CsvIcon />
            {t('common.page.table.exportCSV')}
          </Button>
        </div>
        <DataTablePagination table={table} />
      </div>
    </DataTable>
  );
}
